/**
 * M7 of RAILGUN_BALANCE_SYNC_REFACTOR_PLAN_VI.md.
 *
 * Reads the atomic `AccountBalanceSnapshot.overallStatus` published by the SW and
 * renders a small per-account badge. Per-token row icons are intentionally NOT in
 * scope here — those live alongside the token list and read `chains[chainId].status`.
 *
 * Pure presentational; data plumbing is via `useRailgunStatusBadgeData()`.
 */
import React, { useEffect, useMemo, useState } from 'react'
import { View, Text, StyleSheet } from 'react-native'
import type { TFunction } from 'i18next'

import { useTranslation } from '@common/config/localization'
import { i18nKey } from '@common/config/localization/constants'

export type RailgunOverallStatus =
  | 'verified'
  | 'syncing'
  | 'partial'
  | 'stale'
  | 'poisoned'
  | 'error'

const COLOR_BY_STATUS: Record<RailgunOverallStatus, string> = {
  verified: '#17c964',
  syncing: '#f5a524',
  partial: '#f5a524',
  stale: '#9aa3b2',
  poisoned: '#f31260',
  error: '#f31260'
}

// Translucent pill background that reads well over both the dark dashboard
// gradient and lighter card surfaces.
const PILL_BG = 'rgba(0, 0, 0, 0.32)'
const PILL_BORDER = 'rgba(255, 255, 255, 0.08)'

// i18n keys — translated at render.
const LABEL_BY_STATUS: Record<RailgunOverallStatus, string> = {
  verified: i18nKey('Verified'),
  syncing: i18nKey('Syncing'),
  partial: i18nKey('Partial'),
  stale: i18nKey('Stale'),
  poisoned: i18nKey('Cache poisoned'),
  error: i18nKey('Error')
}

// Full "Last updated …" sentences (i18n: no word-level splicing).
function formatLastUpdated(t: TFunction, refreshedAt: number, now: number): string {
  if (!refreshedAt) return t('Last updated never')
  const sec = Math.max(0, Math.floor((now - refreshedAt) / 1000))
  if (sec < 60) return t('Last updated recently')
  const min = Math.floor(sec / 60)
  if (min < 60) return t('Last updated {{minutes}}m ago', { minutes: min })
  const hr = Math.floor(min / 60)
  if (hr < 24) return t('Last updated {{hours}}h ago', { hours: hr })
  return t('Last updated {{days}}d ago', { days: Math.floor(hr / 24) })
}

// Re-tick `now` while the popup is open so the relative age stays fresh
// (`useMemo` over `Date.now()` alone would freeze at first render). 30s is fine
// since the smallest unit we show is minutes.
function useNow(intervalMs = 30_000): number {
  const [now, setNow] = useState(() => Date.now())
  useEffect(() => {
    const id = setInterval(() => setNow(Date.now()), intervalMs)
    return () => clearInterval(id)
  }, [intervalMs])
  return now
}

export type RailgunStatusBadgeProps = {
  status: RailgunOverallStatus
  refreshedAt: number
  coverage: {
    requested: number[]
    verified: number[]
    scanning: number[]
    stale: number[]
    error: number[]
    poisoned: number[]
  }
  /** Map chainId → human name. Optional; falls back to `chain N`. */
  getChainName?: (chainId: number) => string
  onPress?: () => void
}

const RailgunStatusBadge: React.FC<RailgunStatusBadgeProps> = ({
  status,
  refreshedAt,
  coverage,
  getChainName,
  onPress
}) => {
  const { t } = useTranslation()
  const color = COLOR_BY_STATUS[status]
  const now = useNow()
  const label = useMemo(() => {
    const name = (id: number) =>
      getChainName ? getChainName(id) : t('chain {{chainId}}', { chainId: id })
    switch (status) {
      case 'verified':
        return t(LABEL_BY_STATUS.verified)
      case 'syncing':
        return coverage.scanning.length
          ? t('Updating {{chains}}…', { chains: coverage.scanning.map(name).join(', ') })
          : t(LABEL_BY_STATUS.syncing)
      case 'partial':
        return coverage.error.length
          ? t('Partial ({{chains}} unavailable)', { chains: coverage.error.map(name).join(', ') })
          : t(LABEL_BY_STATUS.partial)
      case 'stale':
        return formatLastUpdated(t, refreshedAt, now)
      case 'poisoned':
        return coverage.poisoned.length
          ? t('Cache poisoned ({{chains}})', { chains: coverage.poisoned.map(name).join(', ') })
          : t(LABEL_BY_STATUS.poisoned)
      case 'error':
        return t('Unable to refresh — retry')
      default:
        return t(LABEL_BY_STATUS[status])
    }
  }, [status, refreshedAt, coverage, getChainName, now, t])

  return (
    <View
      // @ts-ignore — RN-Web accepts onClick via Pressable wrapper at call sites; keep this presentational.
      onClick={onPress}
      style={styles.container}
    >
      <View style={[styles.dot, { backgroundColor: color }]} />
      {/* Badge sits on the dark/gradient banner: label always white, the dot keeps
          the per-status color so it still signals verified/syncing/error at a glance. */}
      <Text style={[styles.text, { color: '#fff' }]} numberOfLines={1}>
        {label}
      </Text>
    </View>
  )
}

const styles = StyleSheet.create({
  container: {
    flexDirection: 'row',
    alignItems: 'center',
    paddingHorizontal: 10,
    paddingVertical: 4,
    borderRadius: 999,
    borderWidth: 1,
    borderColor: PILL_BORDER,
    backgroundColor: PILL_BG
  },
  dot: {
    width: 6,
    height: 6,
    borderRadius: 3,
    marginRight: 6
  },
  text: {
    fontSize: 11,
    fontWeight: '600',
    letterSpacing: 0.2
  }
})

export default RailgunStatusBadge
